import { Dialog, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import Select from "components/common/Select";
import Input from "components/common/Input";
import Button from "components/common/Button";
import api from "config/api";
import moment from "moment";
import { toast } from "react-toastify";

type Props = {
  open: boolean;
  onClose: () => void;
  restDateId: boolean | number;
  refetch: () => void;
  isMultiDatesAdd: boolean;
  selectedData: any;
};

const DateSettingDialog = ({
  open,
  onClose,
  restDateId,
  refetch,
  isMultiDatesAdd,
  selectedData,
}: Props) => {
  const [multipleFormData, setMultipleFormData] = useState([
    {
      TimdId: 0,
      TimdOrdtype: "",
      TimdEnable: 1,
      TimdFrom: moment().format("YYYY-MM-DD"),
      TimdTo: moment().add(1, "days").format("YYYY-MM-DD"),
    },
  ]);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setMultipleFormData([
      {
        TimdId: 0,
        TimdOrdtype: "",
        TimdEnable: 1,
        TimdFrom: moment().format("YYYY-MM-DD"),
        TimdTo: moment().add(1, "days").format("YYYY-MM-DD"),
      },
    ]);
    onClose();
  };

  const createRestDate = () => {
    setLoading(true);

    if (!multipleFormData.every((item) => item.TimdOrdtype)) {
      toast.error("Select order type is required");
      setLoading(false);
      return;
    } else if (
      !multipleFormData.every(
        (item) =>
          moment(item.TimdFrom).format("YYYY-MM-DDT00:00:00") >=
            moment().format("YYYY-MM-DDT00:00:00") &&
          moment(item.TimdTo).format("YYYY-MM-DDT00:00:00") >=
            moment(item.TimdFrom).format("YYYY-MM-DDT00:00:00")
      )
    ) {
      toast.error("Select current date or above in current year");
      setLoading(false);
      return;
    } else if (
      !multipleFormData.every((item) => item.TimdFrom && item.TimdTo)
    ) {
      toast.error("from date and to date is required");
      setLoading(false);
      return;
    }
    if (isMultiDatesAdd) {
      const payload = multipleFormData.map((elem) => ({
        ...elem,
        TimdFrom: moment(elem.TimdFrom).format("YYYY/MM/DD HH:mm:ss"),
        TimdTo: moment(elem.TimdTo).format("YYYY/MM/DD HH:mm:ss"),
      }));
      api
        .post("api/RestDates/AddRestdates", payload)
        .then(() => {
          refetch();
          reset();
          toast.success("Rest Date created successfully");
        })
        .catch(() => toast.error("Something went wrong!"))
        .finally(() => setLoading(false));
    } else {
      if (typeof restDateId === "number") {
        api
          .post("api/RestDates/UpdateRestdate", {
            ...multipleFormData[0],
            TimdFrom: moment(multipleFormData[0].TimdFrom).format(
              "YYYY/MM/DD HH:mm:ss"
            ),
            TimdTo: moment(multipleFormData[0].TimdTo).format(
              "YYYY/MM/DD HH:mm:ss"
            ),
          })
          .then(() => {
            refetch();
            reset();
            toast.success("Rest Date updated successfully");
          })
          .catch(() => toast.error("Something went wrong!"))
          .finally(() => setLoading(false));
      } else {
        api
          .post("api/RestDates/AddRestdate", {
            ...multipleFormData[0],
            TimdFrom: moment(multipleFormData[0].TimdFrom).format(
              "YYYY/MM/DD HH:mm:ss"
            ),
            TimdTo: moment(multipleFormData[0].TimdTo).format(
              "YYYY/MM/DD HH:mm:ss"
            ),
          })
          .then(() => {
            refetch();
            reset();
            toast.success("Reset Date created successfully");
          })
          .catch(() => toast.error("Something went wrong!"))
          .finally(() => setLoading(false));
      }
    }
  };

  const addMoreFields = () => {
    setMultipleFormData((prev) => [
      ...prev,
      {
        TimdId: 0,
        TimdOrdtype: "",
        TimdEnable: 1,
        TimdFrom: moment().format("YYYY-MM-DD"),
        TimdTo: moment().add(1, "days").format("YYYY-MM-DD"),
      },
    ]);
  };

  const deleteFormData = (i: number) => {
    setMultipleFormData((prev) => prev.filter((_, index) => index !== i));
  };

  useEffect(() => {
    if (restDateId && selectedData) {
      setMultipleFormData([
        {
          TimdId: selectedData.timdId,
          TimdEnable: selectedData.timdEnable,
          TimdOrdtype: selectedData.timdOrdtype,
          TimdFrom: moment(selectedData.timdFrom).format("YYYY-MM-DD"),
          TimdTo: moment(selectedData.timdTo).format("YYYY-MM-DD"),
        },
      ]);
    }
  }, [restDateId, selectedData]);

  return (
    <Dialog open={open} onClose={reset}>
      <div className="min-w-[80vw] md:min-w-[600px] min-h-[70vh] py-2.5 flex flex-col">
        <h4 className="!text-lg text-primary font-semibold text-center border-b pb-2">
          {typeof restDateId === "number" ? "Update Dates" : "Add Dates"}
        </h4>
        <div className="border-b px-4 py-0.5 text-[15px] flex-1 flex flex-col gap-3 pt-3">
          {multipleFormData.map((item, i) => (
            <div
              className={`flex flex-col gap-3 p-4 relative rounded-lg ${
                isMultiDatesAdd ? "bg-primary/20" : ""
              }`}
              key={i}
            >
              {isMultiDatesAdd && (
                <div
                  className="absolute bg-primary px-2 py-[2px] rounded-full text-white font-bold -right-[8px] -top-[10px] cursor-pointer"
                  onClick={() => deleteFormData(i)}
                >
                  X
                </div>
              )}
              <Select
                label="Order Type"
                value={item.TimdOrdtype}
                onValueChange={(TimdOrdtype) => {
                  setMultipleFormData((e) => {
                    const old = [...multipleFormData];
                    old[i].TimdOrdtype = TimdOrdtype;
                    return old;
                  });
                }}
                key={i}
              >
                <MenuItem value={"TakeOut"}>TakeOut</MenuItem>
                <MenuItem value={"Delivery"}>Delivery</MenuItem>
                <MenuItem value={"Both"}>Both</MenuItem>
              </Select>
              <div className="flex gap-2 w-full">
                <Input
                  label="From Date"
                  type="date"
                  className="flex-1"
                  value={item.TimdFrom}
                  onValueChange={(TimdFrom) => {
                    setMultipleFormData((e) => {
                      const old = [...multipleFormData];
                      old[i].TimdFrom = TimdFrom;
                      old[i].TimdTo = moment(TimdFrom)
                        .add(1, "days")
                        .format("YYYY-MM-DD");
                      return old;
                    });
                  }}
                />
                <Input
                  label="To Date"
                  type="date"
                  className="flex-1"
                  value={item.TimdTo}
                  onValueChange={(TimdTo) => {
                    setMultipleFormData((e) => {
                      const old = [...multipleFormData];
                      old[i].TimdTo = TimdTo;
                      return old;
                    });
                  }}
                  inputProps={{
                    min: moment(item.TimdFrom).format("YYYY-MM-DD"),
                  }}
                />
              </div>
            </div>
          ))}
          {isMultiDatesAdd && (
            <Button className="mt-3 " onClick={addMoreFields}>
              Add More
            </Button>
          )}
        </div>
        <Button
          className="mt-3 mx-3"
          onClick={createRestDate}
          disabled={loading}
        >
          Submit
        </Button>
      </div>
    </Dialog>
  );
};

export default DateSettingDialog;
